import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogPortal, DialogOverlay } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Tracks the most recent pointer-down position so the modal zooms from the
 * click origin — same technique as gl-advertiser-dashboard's AddCreativeModal.
 */
const lastPointer = { x: -1, y: -1 };
if (typeof window !== 'undefined') {
  window.addEventListener(
    'pointerdown',
    e => {
      lastPointer.x = e.clientX;
      lastPointer.y = e.clientY;
    },
    { capture: true, passive: true }
  );
}

type BaseDrawerProps = {
  children?: React.ReactNode;
  open?: boolean;
  onClose?: () => void;
  className?: string;
};

function BaseDrawer({ children, open, onClose, className }: BaseDrawerProps) {
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (!open) return;
    const el = contentRef.current;
    if (!el || lastPointer.x < 0) return;
    const rect = el.getBoundingClientRect();
    const clamp = (n: number) => Math.max(0, Math.min(100, n));
    const ox = clamp(((lastPointer.x - rect.left) / rect.width) * 100);
    const oy = clamp(((lastPointer.y - rect.top) / rect.height) * 100);
    el.style.transformOrigin = `${ox}% ${oy}%`;
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose?.()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={contentRef}
          data-slot="dialog-content"
          className={cn(
            'bg-card border border-border rounded-2xl overflow-hidden',
            'fixed top-[50%] left-[50%] z-50',
            'translate-x-[-50%] translate-y-[-50%]',
            'w-[calc(100%-2rem)] max-w-2xl max-h-[calc(100vh-4rem)]',
            'flex flex-col shadow-elev-3',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90',
            'duration-500',
            className
          )}
        >
          {/* Sticky header — close button always visible */}
          <div className="shrink-0 border-b border-border px-5 py-3 flex items-center justify-end">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 rounded-md text-muted-foreground hover:text-foreground"
              onClick={onClose}
            >
              <X className="size-4" strokeWidth={2.5} />
            </Button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 min-h-0 overflow-y-auto p-6">{children}</div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export default BaseDrawer;
